import { Layer, MapLayerMouseEvent, MapMouseEvent, Marker, Source, useMap } from "react-map-gl"
import { AreaSuggestions, areaSuggestions, createReferenceAreaFromSuggestion, isReferenceAreaEditable, onAddReferenceAreaVertex, onReferenceAreaVertexMove, onRemoveReferenceAreaVertex, referenceArea, referenceAreaVertices } from "../../appState/referenceAreaSignals"
import { useEffect, useState } from "react"
import { TbMapPinCode } from "react-icons/tb"
import { useSignalEffect } from "@preact/signals-react"
import { layerVisibility } from "../../appState/mapSignals"
import { agriculturalArea } from "../../appState/treeLineSignals"
import { Box } from "@mui/material"

export const ReferenceAreaSource: React.FC = () => {
    // get a reference to the map
    const map = useMap()

    // put the reference are suggestions into a local signal, to add the hover indicator to the properties
    const [suggestions, setSuggestions] = useState<AreaSuggestions>({type: 'FeatureCollection', features: []})
    

    useSignalEffect(() => {
        if (areaSuggestions.value.features.length > 0 && referenceArea.value?.features.length === 0) {
            setSuggestions({
                type: 'FeatureCollection',
                features: areaSuggestions.peek().features.map((f, idx) => ({...f, id: idx}))
            })
        } else {
            setSuggestions({type: 'FeatureCollection', features: []})
        }
    })

    // define some handler
    const handleMouseEnter = (e: MapLayerMouseEvent) => {
        // update the pointer
        if (map.current) {
            map.current.getCanvas().style.cursor = 'pointer'
        }

        // update the features
        if (e.features!.length > 0) {
            map.current!.setFeatureState({source: 'reference-suggestions', id: e.features![0].id}, {hover: true})
        }
    }

    const handleMouseLeave = (e: MapLayerMouseEvent) => {
        // update the pointer
        if (map.current) {
            map.current.getCanvas().style.cursor = ''
        }

        // get the source data and reset the hover state
        suggestions.features.forEach(f => {
            map.current!.setFeatureState({source: 'reference-suggestions', id: f.id}, {hover: false})
        })
    }

    const handleClick = (e: MapLayerMouseEvent) => {
        // get the clicked feature
        const clickedFeature = e.features![0]
        if (!clickedFeature) return

        // create a referenceArea from the clicked feature
        createReferenceAreaFromSuggestion(clickedFeature.properties!.id)
    }

    const handleAddVertex = (e: MapMouseEvent) => {
        if (isReferenceAreaEditable.peek()) {
            onAddReferenceAreaVertex([e.lngLat.lng, e.lngLat.lat])
        }
    }

    // register the handlers to the map
    useEffect(() => {
        if (map.current) {
            map.current.on('mouseenter', 'reference-suggestions', handleMouseEnter)
            map.current.on('mouseleave', 'reference-suggestions', handleMouseLeave)
            map.current.on('click', 'reference-suggestions', handleClick)
            map.current.on('click', handleAddVertex)
        }

        return () => {
            map.current!.off('mouseenter', 'reference-suggestions', handleMouseEnter)
            map.current!.off('mouseleave', 'reference-suggestions', handleMouseLeave)
            map.current!.off('click', 'reference-suggestions', handleClick)
            map.current!.off('click', handleAddVertex)
        }
    }, [suggestions])

    return <>
        <Source 
            id="reference-suggestions" 
            type="geojson" 
            data={suggestions} 
            // generateId
        >
            <Layer 
                id="reference-suggestions" 
                source="reference-suggestions" 
                type="fill"
                paint={{
                    // use the feature state to style the hover effect
                    'fill-color': ['case', ['boolean', ['feature-state', 'hover'], false], '#FF0000', '#FFA07A'],
                    'fill-opacity': 0.5,
                }} 
            />
        </Source>
        <Source 
            id="reference-area"
            type="geojson"
            data={referenceArea.value}
            generateId
        >
            <Layer 
                id="reference-area"
                source="reference-area"
                type="line"
                layout={{'visibility': layerVisibility.value['referenceArea'] === 'visible' ? 'visible' : 'none'}}
                paint={{
                    'line-color': 'purple',
                    'line-width': 9,
                
                }}
            />
        </Source>
        <Source
            id="agriculture-area"
            type="geojson"
            data={agriculturalArea.value}
            generateId
        >
            <Layer 
                id="agriculture-area"
                source="agriculture-area"
                type="fill"
                layout={{'visibility': layerVisibility.value['referenceArea'] === 'visible' ? 'visible' : 'none'}}
                paint={{
                    'fill-color': '#FFA07A',
                    // 'fill-color': 'purple',
                    'fill-opacity': 0.2,
                }}
                beforeId="tree-locations"
            />
            <Layer 
                id="agriculture-area-line"
                source="agriculture-area"
                type="line"
                layout={{'visibility': layerVisibility.value['referenceArea'] === 'visible' ? 'visible' : 'none'}}
                paint={{
                    'line-color': '#FFA07A',
                    // 'line-color': 'purple',
                    'line-width': 3,
                }}
            />
        </Source>

        {/* add the vertices as Marker */}
        { isReferenceAreaEditable.value ? referenceAreaVertices.value.map(vertex => (
            <Marker 
                key={vertex.idx}
                longitude={vertex.lon}
                latitude={vertex.lat}
                onDrag={e => onReferenceAreaVertexMove(vertex.idx, [e.lngLat.lng, e.lngLat.lat])}
                draggable
                anchor="bottom"
            >   
                <Box 
                    sx={{fontSize: '1.5rem', color: 'white', '&:hover': {fontSize: '1.8rem', color: 'red'}}}
                    onContextMenu={e => {e.preventDefault();onRemoveReferenceAreaVertex(vertex.idx)}}
                >
                <TbMapPinCode />
                </Box>
            </Marker>
        )) : null}
    </>
}

export default ReferenceAreaSource